<!-- @format -->

<template>
	<div
		:class="
			active
				? 'widget-select-active'
				: item.category === 'container'
				? 'widget-select-container'
				: 'widget-select-field'
		"
	>
		<slot></slot>
		<template v-if="active">
			<div v-if="canSort" class="widget-icon widget-drag">
				<el-icon size="16">
					<Rank />
				</el-icon>
				<span>{{ fieldLabel }}</span>
				<el-icon v-if="item.pcProps.hidden" size="14">
					<Eye />
				</el-icon>
			</div>
			<div v-if="canDelete" class="widget-icon widget-delete" @click.stop="emit('delete')">
				<el-icon size="16">
					<DeleteFilled />
				</el-icon>
			</div>
		</template>
		<template v-else>
			<div v-if="item.pcProps.hidden" class="widget-icon-hidden">
				<el-icon size="14">
					<Eye />
				</el-icon>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
	import { useDragStoreWithOut } from '../../store';
	import { useBaseConfigStoreWithOut } from '../../config/widget';
	import type { FieldsType } from '../../types';

	interface Props {
		item: FieldsType;
		canDelete?: boolean | ((a: any) => boolean);
		canSort?: boolean | ((a: any) => boolean);
	}

	const props = withDefaults(defineProps<Props>(), {
		canDelete: true,
		canSort: true,
	});

	const emit = defineEmits(['delete']);

	const name = inject<string>('name');

	const dragStore = useDragStoreWithOut(name);
	const baseConfigStore = useBaseConfigStoreWithOut(name);

	const active = computed(() => dragStore.selectField?.code === props.item.code);

	const FLAT_FIELDS = computed(() => baseConfigStore.FIELDS.reduce((arr, itm) => [...arr, ...itm.children], []));

	const fieldLabel = computed(() => FLAT_FIELDS.value.find((itm) => itm.type === props.item.type)?.pcProps.label);

	const canDelete = computed(() =>
		typeof props.canDelete === 'function' ? props.canDelete(props.item) : props.canDelete,
	);

	const canSort = computed(() => (typeof props.canSort === 'function' ? props.canSort(props.item) : props.canSort));
</script>

<style scoped lang="scss">
	// 解决容器带border导致字段移入时经过边框导致字段抖动问题，字段移到边框时draggable会认为是移入当前字段上方，所以产生抖动，去掉边框该用outline即可
	.widget-select-container {
		outline: 1px dashed var(--el-border-color-light);
		position: relative;

		&:hover {
			outline: 1px dashed var(--el-color-primary);
		}
	}

	.widget-select-field {
		border: 1px dashed var(--el-border-color-light);
		position: relative;

		&:hover {
			border: 1px dashed var(--el-color-primary);
		}
	}

	.widget-select-container,
	.widget-select-field {
		.widget-icon-hidden {
			top: 0;
			left: 0;
			position: absolute;
			z-index: 2;
			background-color: var(--el-color-primary);
			color: #fff;
			line-height: 10px;
			padding: 5px;
		}
	}

	.widget-select-active {
		position: relative;
		border: 2px solid var(--el-color-primary);

		.widget-icon {
			position: absolute;
			z-index: 2;
			background-color: var(--el-color-primary);
			color: #fff;
			padding: 5px;
			line-height: 10px;
		}

		.widget-drag {
			top: 0;
			left: 0;
			cursor: move;
			display: flex;
			align-items: center;

			span {
				font-size: 14px;
				margin: 0 5px;
			}
		}

		.widget-delete {
			right: 0;
			bottom: 0;
			cursor: pointer;
		}
	}
</style>
